import { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { sortBy } from 'ramda'

import { Address } from '@ecm/data/Address'
import { Faq } from '@ecm/data/Faq'
import { Plan } from '@ecm/data/Plan'
import { runEff } from '@ecm/effect'
import { trackPricingPlans } from '@ecm/effect/client/Ecommerce/TrackEcommerce'
import { PricingTabKind } from '@ecm/pages/pricing'
import Dashboard from '@ecm/ui/component/block/Dashboard'
import FaqBlock from '@ecm/ui/component/block/Faq'
import PlansBlock from '@ecm/ui/component/block/PlansBlock'
import PlanTabsBlock from '@ecm/ui/component/block/PlanTabsBlock'
import VirtualMailHowItWorks from '@ecm/ui/component/block/VirtualMailHowItWorks'
import NeedHelp from '@ecm/ui/component/NeedHelp'
import TrustBanner from '@ecm/ui/component/TrustBanner'
import VirtualMailbox from '@ecm/ui/component/VirtualMailbox'
import Features from '@ecm/ui/component/virtualMailbox/Features'
import VirtualMailboxBanner from '@ecm/ui/component/VirtualMailboxBanner'
import Color from '@ecm/ui/element/Color'
import Hero from '@ecm/ui/element/Hero'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'
import { filterPlans } from '@ecm/utils/filterPlans'

import automateIcon from '/public/images/icon/automate-2.svg'
import noThirdPartyIcon from '/public/images/icon/no-third-party.svg'
import storageIcon from '/public/images/icon/storage.svg'
import supportIcon from '/public/images/icon/support.svg'
import globeIllustration from '/public/images/illustration/globe.svg'
import headerIllustration from '/public/images/illustration/landing-page-header.svg'

type MailRoomPageProps = {
  faqs: Faq[]
  plans: Plan[]
  beavertonAddress: Address
}

const HowItWorksContent = [
  {
    content: 'Choose one of our real, physical addresses to use as your mailing address.',
    heading: 'Select an address',
    icon: 'location-pin',
  },
  {
    content: 'Your postal mail is sent to the selected address and securely processed.',
    heading: 'We receive your mail',
    icon: 'mail',
  },
  {
    content: 'Your postal mail is scanned and delivered into your inbox as a high-resolution PDF.',
    heading: 'We digitize it for you',
    icon: 'scan',
  },
  {
    content:
      'View your postal mail and access our check depositing service from anywhere, on any device, at any time.',
    heading: 'Access your mail online',
    icon: 'earth',
  },
]

const pros = [
  {
    icon: automateIcon,
    title: (
      <>
        <b>Automate</b> your mail to work for you
      </>
    ),
  },
  {
    description: 'only our trained staff (unlike our competitors)',
    icon: noThirdPartyIcon,
    title: <b>No third party is handling your mail,</b>,
  },
  {
    icon: supportIcon,
    title: (
      <>
        <b>Live</b> Customer Support
      </>
    ),
  },
  {
    icon: storageIcon,
    title: (
      <>
        <b>Unlimited</b> Cloud Storage
      </>
    ),
  },
]

const MailRoomPage = ({ faqs, plans }: MailRoomPageProps) => {
  const router = useRouter()
  const [selectedTab, setSelectedTab] = useState<PricingTabKind>('mailbox')
  const { mailBoxPlans, mailRoomPlans } = filterPlans(plans)

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    if (plans.length) {
      trackPricingPlans(plans)
    }
  }, [router.isReady, plans])

  return (
    <DefaultLayout seoPage="virtual-mailbox" hideFinalCta>
      <Box
        sx={{
          background: Color.smoke.normal,
          h1: {
            fontSize: { md: '64px', xs: '42px' },
            lineHeight: { md: '76px', xs: '56px' },
          },
        }}
      >
        <Hero heading="Access your virtual mail from anywhere" image={headerIllustration}>
          <Typography variant="p3">
            <b>
              Receive your postal mail at one of our addresses and get digitized, high-quality,
              high-resolution, color PDFs
            </b>{' '}
            uploaded to your virtual mailbox. Log into your account to search, sort, sync, share, or
            securely shred your postal mail.
          </Typography>
        </Hero>
      </Box>
      <PlanTabsBlock selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      {selectedTab === 'mailbox' && (
        <PlansBlock plans={sortBy(p => p.price, mailBoxPlans)} router={router} bottomBanner />
      )}
      {selectedTab === 'mailroom' && (
        <PlansBlock plans={sortBy(p => p.price, mailRoomPlans)} router={router} bottomBanner />
      )}

      <MainContainer>
        <VirtualMailHowItWorks items={HowItWorksContent} />
      </MainContainer>

      <VirtualMailboxBanner />

      <Box bgcolor={Color.smoke.normal}>
        <VirtualMailbox illustration={globeIllustration} />
      </Box>

      <Features />

      <Dashboard
        heading="What makes our virtual mailbox different?"
        subHeading="Easy to Navigate Mailroom Dashboard — Even on a Mobile Device!"
        steps={pros}
        headingMaxWidth="480px"
      />

      <TrustBanner text="Access your mail from anywhere, on any device, at any time — with more automations, support, and storage than ever before." />

      <FaqBlock faqs={faqs} />

      <NeedHelp message="There are several ways you can" />
    </DefaultLayout>
  )
}

export default MailRoomPage

export const getStaticProps: GetStaticProps = async () => {
  const isPreview = false // Static content mode - no preview
  const [plans, faqs]: [Plan[], Faq[]] = await runEff(async eff => {
    return Promise.all([eff.getPlans(isPreview), eff.getFaqsByPage(['/mailbox'])])
  })

  return { props: { faqs, plans } }
}
